import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { selectExpenseReportsBodyFromState } from 'src/app/ApproverExpenseReports/redux/approverExpenseReports.selectors'
import { selectIsToastOpenFromState } from 'src/app/Toast/redux/toast.selectors'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ConnectionOption from 'src/components/ConnectionOption/ConnectionOption'
import ConnectionOptionsContainer from 'src/components/ConnectionOptionsContainer/ConnectionOptionsContainer'
import ErrorList from 'src/components/ErrorList/ErrorList'
import Popup from 'src/components/Popup/Popup'
import RadioItems from 'src/components/RadioItems/RadioItems'
import { downloadOptions } from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { createSetIsDownloadExpenseReportsPaidPopupOpen } from 'src/redux/actions/app.actions'
import {
  CLEAR_TOP_LEVEL_ERROR_EXPENSE_REPORTS,
  postExpenseReportDownload,
} from 'src/redux/actions/data/reportDownloads.actions'
import {
  selectReportDownloadLoadingFromState,
  selectTopLevelErrorFromState,
} from 'src/redux/selectors/data/reportDownloads.selectors'
import { css } from 'styled-components'

export interface PostExpenseReportsDownloadRequestParams {
  search?: string
  statuses?: string[]
  userIds?: string[]
  sinceTimestamp?: string
  untilTimestamp?: string
  attachments?: boolean
}

const DownloadExpenseReports: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const filtersBody = useSelector(selectExpenseReportsBodyFromState)
  const reportDownloadLoading = useSelector(
    selectReportDownloadLoadingFromState,
  )
  const isToastOpen = useSelector(selectIsToastOpenFromState)
  const topLevelError = useSelector(selectTopLevelErrorFromState)

  const dispatch = useFlyoutDispatch()
  const translate = useTranslate()

  const [includeAttachments, setIncludeAttachments] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const onSubmit = (): void => {
    dispatch(
      postExpenseReportDownload({
        search: filtersBody?.search,
        statuses: filtersBody?.statuses,
        sinceTimestamp: filtersBody?.sinceTimestamp,
        untilTimestamp: filtersBody?.untilTimestamp,
        userIds: filtersBody?.userIds,
        attachments: includeAttachments,
      }),
    )
  }
  const csvOption = [
    <span key={downloadOptions.csv}>
      <p
        css={css`
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
        `}
      >
        {translate('CSV')}
      </p>
      <p
        css={css`
          ${getFontCss(
            14,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )}
          margin-top: 8px;
        `}
      >
        {translate(
          'Note: When opening in Excel, be sure to change the file origin to Unicode (UTF-8) to avoid formatting errors',
        )}
      </p>
    </span>,
  ]

  const closePopup = (): void => {
    dispatch(createSetIsDownloadExpenseReportsPaidPopupOpen(false))
    dispatch({ type: CLEAR_TOP_LEVEL_ERROR_EXPENSE_REPORTS })
  }

  return (
    <Popup
      onClose={closePopup}
      css={css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      `}
    >
      <span
        css={css`
          ${getFontCss(
            32,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
          margin-bottom: 40px;
        `}
      >
        {translate('Download Reimbursement Reports')}
      </span>
      <RadioItems
        css={css`
          width: 100%;
        `}
        items={csvOption}
        currentIndex={selectedIndex}
        setIndex={setSelectedIndex}
        grayBackground
        useRadioItemStyle
        ariaLabels={[translate('CSV')]}
      />
      <ConnectionOptionsContainer
        css={css`
          margin-top: 40px;
          border: 1px solid ${styleConstants.colors.gray_medium};
        `}
        showErrors={topLevelError !== undefined}
      >
        <ConnectionOption
          css={css`
            background-color: ${styleConstants.colors.gray_light};
          `}
          onClick={(): void => {
            setIncludeAttachments(!includeAttachments)
          }}
          isSelected={includeAttachments}
          isCheckbox={true}
        >
          {translate(
            'Include attachments associated with the reimbursement reports you wish to download (200 attachments max).',
          )}
        </ConnectionOption>
      </ConnectionOptionsContainer>
      {topLevelError !== undefined && <ErrorList errors={[topLevelError]} />}
      <Button
        css={css`
          margin-top: 40px;
        `}
        type={ButtonTypes.button}
        design={ButtonDesigns.designCta}
        big
        text={
          includeAttachments
            ? translate('Download CSV + Attachments')
            : translate('Download CSV')
        }
        testId={'downloadBtn'}
        onClick={onSubmit}
        disabled={
          selectedIndex === undefined || reportDownloadLoading || isToastOpen
        }
        loading={reportDownloadLoading || isToastOpen}
        loadingText={translate('Preparing Files')}
      />
    </Popup>
  )
}

export default DownloadExpenseReports
